import { type JSXElementConstructor, ReactNode } from 'react';
import { FieldValues, Path } from 'react-hook-form';
import { To } from 'react-router-dom';

import {
  Customization as EnterpriseCustomization,
  type InventoryAllocationResponse as EnterpriseInventoryAllocationResponse,
  SearchDescription,
  type ShipmentAddressBulkOverrideResponse as ECBulkAddressOverrideResponse,
  type ShipmentBulkDeleteResponse as ECBulkDeleteResponse,
} from '@libraries/api/gen-api';
import {
  AdminInventoryAllocationResponse,
  ChannelPartnerProductInventory,
  ChannelPartnerResultSet,
  type CustomizationResponse as ServiceCustomizationResponse,
  CustomProductRedeemTier,
  Organization as AdminOrganization,
  OrgNameAndNetsuiteID,
  Product,
  RefundReasonResponse,
  type ShipmentAddressBulkOverrideResponse as SCBulkAddressOverrideResponse,
  type ShipmentBulkDeleteResponse as SCBulkDeleteResponse,
} from '@libraries/api/gen-api-admin';
import { FulfillmentPartnersResponse, FulfillmentRuleResultSet } from '@libraries/api/gen-api-fulfillment';
import { InventoryAllocationResponse as HiddenInventoryAllocationResponse } from '@libraries/api/gen-api-hidden';
import { OrganizationPermission, ServicePermission } from '@libraries/api/gen-permelo-enums';

import { SvgProps } from '../client/components/atoms/SubscriptionIcons/subscriptionSvgTypes';

import {
  ORG_ADMIN,
  ORG_AUDITOR,
  ORG_DISTRIBUTOR,
  ORG_OWNER,
  ORG_RESELLER,
  SERVICE_ADMIN,
  SERVICE_AUDITOR,
  SERVICE_OWNER,
} from '../client/constants/roles';

export enum InhouseFulfillmentPartners {
  PA = 'inhouse-pa',
  STHLM = 'inhouse-sthlm',
}

export enum AllShipmentStatuses {
  ShipmentStateIncomplete = 1,
  ShipmentStateDraft = 2,
  ShipmentStateAwaitingValidation = 3,
  ShipmentStateProcessingAddress = 4,
  ShipmentStateAddressValid = 5,
  ShipmentStateAddressInvalid = 6,
  ShipmentStateAddressFail = 7,
  ShipmentStateError = 8,
  ShipmentStateDPLMatch = 9,
  ShipmentStateAwaitingEstimate = 10,
  ShipmentStateAwaitingEstimateApproval = 11,
  ShipmentStateRequested = 12,
  ShipmentStateAwaitingDPLVerification = 13,
  ShipmentStateUnableToFormatAddress = 14,
  ShipmentStatePanic = 99,
  ShipmentStateProcessingShipment = 100,
  ShipmentStateFulfillmentReady = 101,
  ShipmentStateProcessingFulfillment = 102,
  ShipmentStateQueuedForFulfillment = 1025,
  ShipmentStateShipped = 103,
  ShipmentStateDelivered = 104,
  ShipmentStateLost = 105,
  ShipmentStateDeliveryException = 106,
  ShipmentStateDPLConfirmed = 107,
  ShipmentStateCancelled = 108,
  ShipmentStateEstimateAccepted = 109,
  ShipmentStateReturnedToSender = 110,
  ShipmentStateManualFulfillment = 2000,
}

export type ShipmentSourceStates =
  | AllShipmentStatuses.ShipmentStateShipped
  | AllShipmentStatuses.ShipmentStateDelivered
  | AllShipmentStatuses.ShipmentStateLost
  | AllShipmentStatuses.ShipmentStateDeliveryException
  | AllShipmentStatuses.ShipmentStateProcessingFulfillment
  | AllShipmentStatuses.ShipmentStateCancelled
  | AllShipmentStatuses.ShipmentStateRequested;

export interface AddressType {
  recipient_firstname?: string;
  recipient_lastname?: string;
  recipient?: string;
  street_line1?: string;
  street_line2?: string;
  city?: string;
  region?: string;
  postal_code?: string;
  country_code_2?: string;
  recipient_telephone?: string;
  recipient_email: string;
}
export interface ReplacementAddressType {
  recipient_firstname?: string;
  recipient_lastname?: string;
  recipient?: string;
  street_line1: string;
  street_line2?: string;
  city: string;
  region?: string;
  postal_code: string;
  country_code_2: string;
  recipient_telephone?: string;
  recipient_email?: string;
}

export const PACKAGE_TYPE_TRAYS = 'trays';
export const PACKAGE_TYPE_BLISTER = 'blister-packs';
export type PackageTypes = typeof PACKAGE_TYPE_TRAYS | typeof PACKAGE_TYPE_BLISTER;
export type DeliveryType = 'standard' | 'expedited';

export type CustomerRole =
  | typeof ORG_OWNER
  | typeof ORG_ADMIN
  | typeof ORG_AUDITOR
  | typeof ORG_RESELLER
  | typeof ORG_DISTRIBUTOR;
export type ServiceRole = typeof SERVICE_OWNER | typeof SERVICE_ADMIN | typeof SERVICE_AUDITOR;
export type AllRoles = CustomerRole | ServiceRole;

export type RequestState = 'idle' | 'pending' | 'resolved' | 'rejected';

export type RefundReasonSelection = keyof typeof RefundReasonResponse;
export interface CustomProductRedeemTiers {
  [tier: string]: CustomProductRedeemTier;
}

/** @deprecated */
export type SmartTableContext = {
  total_count?: number;
  response_count?: number;
  rows?: any[];
  sort?: string;
  reverse?: boolean;
  advanced_search?: boolean;
  search?: string | string[];
  page?: number;
  pageSize?: number;
};

/** @deprecated */
export type SortButtonProps = {
  column: string;
  children: ReactNode;
};

export type SortDirection = 'ASC' | 'DESC';

export type SearchParams = {
  sortBy?: string;
  sortDirection?: SortDirection;
  page?: number;
  pageSize?: number;
  search?: string | Array<string>;
  searchField?: string | Array<string>;
};

export type SearchResponse<TResponseData> = {
  totalCount: number;
  responseCount: number;
  rows: Array<TResponseData>;
};

export type SearchState<TResponseData> = {
  searchParams: SearchParams;
  searchDescription?: SearchDescription;
  searchResponse?: SearchResponse<TResponseData>;
};

export type AdvancedSearchDateOperation = 'range';
export type AdvancedSearchStringOperation = 'exact' | 'like';
export type AdvancedSearchStringSelectOperation = 'exact';
export type AdvancedSearchBooleanOperation = '=';
export type AdvancedSearchIntOperation = '=';

export type AdvancedSearchOperations =
  | AdvancedSearchDateOperation
  | AdvancedSearchStringOperation
  | AdvancedSearchStringSelectOperation
  | AdvancedSearchBooleanOperation;

type AdvancedSearchBaseTypeDefinition<TType, TOperations> = {
  type: TType;
  operations: Array<TOperations>;
  labelKey: string;
  readonly?: boolean;
};

export type AdvancedSearchStringDefinition = AdvancedSearchBaseTypeDefinition<
  'string',
  AdvancedSearchStringOperation
> & {
  fields?: Array<string>;
};
export type AdvancedSearchStringSelectDefinition = AdvancedSearchBaseTypeDefinition<
  'string-select',
  AdvancedSearchStringSelectOperation
> & {
  options: Array<string>;
};
export type AdvancedSearchDateDefinition = AdvancedSearchBaseTypeDefinition<
  'date',
  AdvancedSearchDateOperation
>;
export type AdvancedSearchBooleanDefinition = AdvancedSearchBaseTypeDefinition<
  'boolean',
  AdvancedSearchBooleanOperation
>;
export type AdvancedSearchIntDefinition = AdvancedSearchBaseTypeDefinition<
  'int',
  AdvancedSearchIntOperation
> & {
  options: Array<[number, string]>;
};

export type AdvancedSearchRuleDefinitions =
  | AdvancedSearchStringDefinition
  | AdvancedSearchStringSelectDefinition
  | AdvancedSearchDateDefinition
  | AdvancedSearchBooleanDefinition
  | AdvancedSearchIntDefinition;

export type AdvancedSearchTypes = AdvancedSearchRuleDefinitions['type'];
export type AdvancedSearchTypeOperations =
  | AdvancedSearchDateOperation
  | AdvancedSearchStringOperation
  | AdvancedSearchStringSelectOperation
  | AdvancedSearchBooleanOperation;

export type AdvancedSearchRules = {
  [field: string]: AdvancedSearchRuleDefinitions;
};

export interface DashboardLocationState {
  backPath: string;
  organization_name: string;
}

export interface AdminChannelPartners {
  loading: boolean;
  channelPartnersResultSet: ChannelPartnerResultSet;
}
export interface ProductMappingProps {
  standardProducts: Array<number>;
  customProductRedeemByTier: CustomProductRedeemTiers;
}

export interface FulfillmentRulesResult {
  loading: boolean;
  fulfillmentRulesResultSet: FulfillmentRuleResultSet;
}

export interface FulfillmentPartnersResult {
  loading: boolean;
  fulfillmentPartnersResponse: FulfillmentPartnersResponse;
}

export interface AdminOrganizationMap {
  [id: string]: AdminOrganization;
}

export interface userFetchParams {
  sort: string;
  reverse: boolean;
  pageSize: number;
  page: number;
}

export interface ParserResult {
  htmlIsValid?: boolean;
  errorText?: string;
  errorLineStart?: number;
  errorLineEnd?: number;
  errorColumnStart?: number;
  errorColumnEnd?: number;
}

export type ShipmentResponseType = ShipmentExactResponseServiceAdmin | ShipmentExactResponse;

export type PurchaseOrderResponse = PurchaseOrder | AdminPurchaseOrder;

export type ChannelPartnerProductInventoryResponseObject = {
  [key: string]: Array<ChannelPartnerProductInventory>;
};

type EditorEvent<TAction extends string> = {
  action: TAction;
};

type EditorEventWithPayload<TAction extends string, TPayload> = EditorEvent<TAction> & {
  payload: TPayload;
};

export type EditorEvents =
  | EditorEvent<'editor-ready'>
  | EditorEventWithPayload<'editor-changed', string>
  | EditorEventWithPayload<'set-value', string>
  | EditorEventWithPayload<'set-markers', ParserResult>
  | EditorEvent<'remove-markers'>;

export type InputSize = 'small' | 'medium' | 'large';

export interface AllInventoryMap {
  [k: string]: number[];
}

/**
 * Type to define data-testid property to testable components.
 * This type serves as an appendix to other types. If other testing
 * properties are needed in the future, we can extend this type to
 * include them and any type extending or including this will
 * also include the new props.
 * @example data-testid: props.testId
 */
export type TestableComponentProps = {
  testId?: string;
};

export interface SideNavItemProps extends TestableComponentProps {
  id: string;
  to?: string;
  title: string;
  disabled?: boolean;
  icon?: ReactNode;
  parentId: string | null;
  roles?: string[];
}

export type SideNavListItemProps = SideNavItemProps & {
  children?: Array<SideNavItemProps>;
};

export type SideNavProps = Array<SideNavListItemProps>;

/** *
 * Bread crumb type
 */

export interface BreadCrumbObject {
  [id: string]: string;
}
export interface BreadCrumbsParamsType {
  [path: string]: BreadCrumbObject;
}

// Snackbar types

/**
 * Describes an action that should be rendered for a given snackbar
 */
export type SnackbarAction = {
  label: ReactNode;
  startIcon?: ReactNode;
  endIcon?: ReactNode;
  onClick?: () => void;
  to?: To;
};

/**
 * The Snackbar can be one of the following
 * variants
 */
export type SnackbarVariant =
  | {
      type: 'simple';
      message: ReactNode;
      action?: SnackbarAction;
    }
  | {
      type: 'extended';
      title: ReactNode;
      message: ReactNode;
      action?: SnackbarAction;
    }
  | {
      type: 'accordion';
      title: ReactNode;
      message: ReactNode;
      action?: SnackbarAction;
      expandLabel?: ReactNode;
    };

// Shipment create types
export type AddressFormData = {
  countryCode2: string;
  postalCode: string;
  streetLine1: string;
  streetLine2?: string;
  streetLine3?: string;
  city: string;
  region: string;
  recipientFirstName?: string;
  recipientLastName?: string;
  /** Corresponds to company */
  recipient?: string;
  recipientTelephone: string;
  recipientEmail?: string;
  addressValidationBypass?: boolean;
  notificationEmail?: string;
};

export type OrganizationsFormData = {
  customerId: string;
  resellerId: string;
  distributorId: string;
};

export type BaseConfigKey = 'default' | `custom:${string}`;

/**
 * A key to represent a product configuration of the form `<product_id>:default` for non-customized products or `<product_id>:custom:<customization_id>` for customized products.
 * The `product_id` can correspond to a virtual product or a physical product.
 * This representation allows us to have a flat object or map for the some of the data needed by the shipment form.
 * */
export type ProductConfigKey = `${number}:${BaseConfigKey}`;

/** A key to represent a shipment item identified by its product ID, form factor ID, and configuration */
export type ShipmentItemConfigKey = `${number}:${ProductConfigKey}`;

/** A product config corresponds to a product customization or lack of (e.g. type = 'default') */
export type BaseShipmentFormFormFactorConfig =
  | { type: 'custom'; customizationID: string; name?: string }
  | { type: 'default' };

export type ShipmentFormFormFactorConfig = {
  hasAllocation: boolean;
  initialQuantity: number;
} & BaseShipmentFormFormFactorConfig;

export type ShipmentFormProduct = {
  productID: number;
  name?: string;
  totalInitialQuantity?: number;
  icon?: JSXElementConstructor<SvgProps>;
  formFactors: Array<{
    productID: number;
    name: string;
    imageCode: string;
    customizationNameOrID?: string;
    configs: Array<ShipmentFormFormFactorConfig>;
  }>;
};

export type BaseShipmentFormDataV2 = {
  /** Formerly channelpartnerID */
  resellerID: number;
  deliveryType: DeliveryType;
  /**
   * Represents a mapping of a product, form factor, and customization configuration to its quantity selected by the user.
   *
   * **NOTE**: The quanity is a string instead of a number for easier interfacing with the native <input> element.
   */
  products: Record<ShipmentItemConfigKey, string>;
} & AddressFormData;

export function configToKey(config: BaseShipmentFormFormFactorConfig): BaseConfigKey {
  return config.type === 'custom' ? `custom:${config.customizationID}` : 'default';
}

export function getProductConfigKey({
  product_id,
  customization_id,
}: {
  product_id: number;
  customization_id: string | null;
}): ProductConfigKey {
  if (customization_id) {
    return `${product_id}:custom:${customization_id}`;
  }

  return `${product_id}:default`;
}

export function getShipmentItemConfigKey(item: {
  inventory_product_id: number;
  product_id: number;
  customization_id: string | null;
}): ShipmentItemConfigKey {
  return `${item.inventory_product_id}:${getProductConfigKey(item)}`;
}

export type ServiceConsoleShipmentFormDataV2 = {
  organizationID: string;
  comment: string;
} & BaseShipmentFormDataV2;

export type ServiceConsoleEditShipmentFormDataV2 = Omit<ServiceConsoleShipmentFormDataV2, 'organizationID'>;
export type ServiceConsoleReplaceShipmentFormDataV2 = Omit<
  ServiceConsoleShipmentFormDataV2,
  'organizationID'
> & {
  notificationEmail: string;
  freeShippingOverride: boolean;
};

export type EnterpriseConsoleShipmentFormDataV2 = BaseShipmentFormDataV2;

export type SubscriptionInventoryAllocationResponse =
  | EnterpriseInventoryAllocationResponse
  | AdminInventoryAllocationResponse
  | HiddenInventoryAllocationResponse;

export interface InventoryAllocationByProductIdResponse {
  [id: string]: SubscriptionInventoryAllocationResponse;
}

export type CustomizationMap = {
  [id: string]: ServiceCustomizationResponse | EnterpriseCustomization;
};

type OrgDeliveryType = {
  name: string;
  value: number;
};

export type OrgCountry = {
  country_code_2: string;
  country_code_3: string;
  country_id: number;
  country_name: string;
  country_vat_rate: number;
  delivery_types: OrgDeliveryType[];
  grouping: string;
  handling: string;
  is_allowed: boolean;
  organization_id: string;
};

export type OrgCountrySet = OrgCountry[];

export type OrgCountryGroup = {
  groupName: string;
  groupCountries: OrgCountrySet;
};

export type OrgCountryGroupSet = OrgCountryGroup[];

type BaseBulkOperationError<TType, TPayload> = {
  type: TType;
  payload: TPayload;
};

export const system = 'system';
export const del = 'delete';
export const addressOverride = 'address-override';

type SystemBulkOperationError = BaseBulkOperationError<'system', any>;

export type ECBulkOperationError =
  | SystemBulkOperationError
  | BaseBulkOperationError<typeof del, ECBulkDeleteResponse>
  | BaseBulkOperationError<typeof addressOverride, ECBulkAddressOverrideResponse>;

export type SCBulkOperationError =
  | SystemBulkOperationError
  | BaseBulkOperationError<typeof del, SCBulkDeleteResponse>
  | BaseBulkOperationError<typeof addressOverride, SCBulkAddressOverrideResponse>;

/** A union of Feature toggle names (or never) that correspond to names in the database */
export type FeatureToggles =
  | 'FEExcludeColumnsCSV'
  | 'FECustomization'
  | 'FECustomizationPhase2'
  | 'FENotifications'
  | 'FENetSuiteSync'
  | 'EndUserPortal'
  | 'Passwordless'
  | 'PasswordlessCompliance'
  | 'FEPermissions'
  | 'FENetSuiteIDAddProductFields'

  // NOTE: Keep `never` type as default when no toggles exist
  | never;

export type OrganizationListMap = {
  [id: string]: OrgNameAndNetsuiteID & { id: string };
};

export type ProductMap = {
  [id: string]: Product;
};

export type FieldPath<TFieldValues extends FieldValues> = Path<TFieldValues>;
export const DEFAULT_VALUE = 'DEFAULT_VALUE';

export type ReadableFileSize = `${number} ${'B' | 'KB' | 'MB' | 'GB'}`;

export type Permission = ServicePermission | OrganizationPermission | string;
